import React, { useContext, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    CssBaseline,
    Grid,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    ThemeProvider,
    Tooltip,
    Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LogoutTwoToneIcon from "@mui/icons-material/LogoutTwoTone";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { ReactComponent as Logo } from "../assets/KaproSCMIcon.svg";
import Copyright from "../components/Copyright";
import LanguageSwitch from "../components/LanguageSwitch";
import DarkModeSwitch from "../components/DarkModeSwitch";
import Notification from "../components/Notification";
import SupplyChainBackdrop from "../components/SupplyChainBackdrop";
import { LanguageContext } from "../components/LanguageContext";
import { darkTheme, lightTheme } from "../theme";
import { login } from "../apis/authApi";
import { setAuth } from "../apis/auth";

const LoginPage = () => {
    const navigate = useNavigate();
    const { translations } = useContext(LanguageContext);

    const [emailError, setEmailError] = useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = useState("");
    const [passwordError, setPasswordError] = useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const [notifType, setNotifType] = useState(null);
    const [notifOpen, setNotifOpen] = useState(false);
    const [notifMessage, setNotifMessage] = useState("");
    const [loading, setLoading] = useState(false);

    const [mode, setMode] = useState(() =>
        localStorage.getItem("theme") === "dark" ? "dark" : "light"
    );
    const theme = mode === "dark" ? darkTheme : lightTheme;

    const handleToggleTheme = () => {
        setMode((prev) => {
            const next = prev === "dark" ? "light" : "dark";
            localStorage.setItem("theme", next);
            return next;
        });
    };

    const validateInputs = () => {
        const email = document.getElementById("email");
        const password = document.getElementById("password");
        let isValid = true;

        if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
            setEmailError(true);
            setEmailErrorMessage(translations.loginEmailInvalid || "Please enter a valid email.");
            isValid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage("");
        }

        if (!password.value || password.value.length < 6) {
            setPasswordError(true);
            setPasswordErrorMessage(
                translations.loginPasswordShort || "Password must be at least 6 characters long."
            );
            isValid = false;
        } else {
            setPasswordError(false);
            setPasswordErrorMessage("");
        }

        return isValid;
    };

    const loginMutation = useMutation({
        mutationFn: (formData) => login(formData),
        onSuccess: (response) => {
            setNotifType(1);
            setNotifMessage(response.data.message);
            setNotifOpen(true);
            setLoading(false);

            setAuth({
                token: response.data.token,
                userId: response.data.user.id,
                roleId: response.data.user.uloga,
            });
            localStorage.setItem("index", 0);

            setTimeout(() => navigate("/home"), 1200);
        },
        onError: (error) => {
            setNotifType(2);
            setNotifMessage(error.response?.data?.message || translations.loginError || "Invalid credentials");
            setNotifOpen(true);
            setLoading(false);
        },
    });

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!validateInputs()) return;
        setLoading(true);
        const data = new FormData(event.currentTarget);
        loginMutation.mutateAsync(data).catch(() => {});
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Notification open={notifOpen} message={notifMessage} setOpen={setNotifOpen} type={notifType} />

            <Box
                sx={(t) => ({
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    px: { xs: 2, sm: 3, md: 5 },
                    py: { xs: 3, md: 5 },
                    background: `
                        radial-gradient(circle at 12% 18%, ${alpha(
                            t.palette.primary.main,
                            t.palette.mode === "dark" ? 0.18 : 0.1
                        )} 0%, transparent 45%),
                        radial-gradient(circle at 88% 82%, ${alpha(
                            t.palette.secondary.main,
                            t.palette.mode === "dark" ? 0.16 : 0.08
                        )} 0%, transparent 45%),
                        ${t.palette.background.default}
                    `,
                })}
            >
                <SupplyChainBackdrop opacity={mode === "dark" ? 0.4 : 0.55} animate />

                <Box
                    sx={{
                        position: "absolute",
                        top: { xs: 16, md: 24 },
                        right: { xs: 16, md: 24 },
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        zIndex: 2,
                    }}
                >
                    <Tooltip title={translations.toggleTheme || "Toggle theme"}>
                        <Box>
                            <DarkModeSwitch onChange={handleToggleTheme} checked={mode === "dark"} />
                        </Box>
                    </Tooltip>
                    <LanguageSwitch />
                </Box>

                <Box
                    sx={(t) => ({
                        position: "relative",
                        zIndex: 1,
                        width: "100%",
                        maxWidth: 1080,
                        bgcolor: "background.paper",
                        borderRadius: { xs: 3, md: 4 },
                        border: `1px solid ${t.palette.divider}`,
                        boxShadow:
                            t.palette.mode === "dark"
                                ? "0 1px 2px rgba(0,0,0,0.4), 0 16px 40px rgba(0,0,0,0.45)"
                                : "0 1px 2px rgba(15,23,42,0.04), 0 16px 40px rgba(15,23,42,0.06)",
                        overflow: "hidden",
                    })}
                >
                    <Grid container>
                        <Grid
                            item
                            xs={12}
                            md={6}
                            sx={{
                                p: { xs: 3, sm: 4, md: 6 },
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                            }}
                        >
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: { xs: 3, sm: 4 } }}>
                                <Box sx={{ width: 48, height: 48 }}>
                                    <Logo style={{ width: "100%", height: "100%" }} />
                                </Box>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {translations.appName || "KaproSCM"}
                                </Typography>
                            </Stack>

                            <Stack spacing={0.75} sx={{ mb: 3 }}>
                                <Typography variant="h3" component="h1">
                                    {translations.loginTitle || "Sign in"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {translations.loginWelcome || "Welcome back!"}
                                </Typography>
                            </Stack>

                            {loginMutation.isError && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {notifMessage || translations.loginError || "Invalid credentials"}
                                </Alert>
                            )}

                            <Box component="form" onSubmit={handleSubmit} noValidate>
                                <Stack spacing={2}>
                                    <TextField
                                        error={emailError}
                                        helperText={emailErrorMessage}
                                        id="email"
                                        name="email"
                                        type="email"
                                        label={translations.loginEmail || "Email address"}
                                        autoComplete="email"
                                        autoFocus
                                        required
                                        fullWidth
                                    />

                                    <TextField
                                        error={passwordError}
                                        helperText={passwordErrorMessage}
                                        name="password"
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        label={translations.loginPassword || "Password"}
                                        autoComplete="current-password"
                                        required
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPassword((v) => !v)}
                                                        edge="end"
                                                        aria-label="toggle password visibility"
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Button
                                        type="submit"
                                        fullWidth
                                        size="large"
                                        variant="contained"
                                        endIcon={loading ? null : <LogoutTwoToneIcon />}
                                        disabled={loading}
                                        sx={{ mt: 1 }}
                                    >
                                        {loading ? (
                                            <CircularProgress size={22} color="inherit" />
                                        ) : (
                                            translations.loginSubmit || "Sign in"
                                        )}
                                    </Button>
                                </Stack>
                            </Box>

                            <Box sx={{ mt: 3 }}>
                                <Copyright />
                            </Box>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            md={6}
                            sx={(t) => ({
                                display: { xs: "none", md: "flex" },
                                alignItems: "center",
                                justifyContent: "center",
                                p: 6,
                                position: "relative",
                                background: `
                                    radial-gradient(circle at 30% 20%, ${alpha(
                                        t.palette.primary.main,
                                        t.palette.mode === "dark" ? 0.28 : 0.18
                                    )} 0%, transparent 55%),
                                    radial-gradient(circle at 80% 80%, ${alpha(
                                        t.palette.secondary.main,
                                        t.palette.mode === "dark" ? 0.26 : 0.18
                                    )} 0%, transparent 55%),
                                    linear-gradient(135deg, ${alpha(
                                        t.palette.primary.main,
                                        t.palette.mode === "dark" ? 0.1 : 0.06
                                    )} 0%, ${alpha(
                                    t.palette.secondary.main,
                                    t.palette.mode === "dark" ? 0.1 : 0.06
                                )} 100%)
                                `,
                                borderLeft: `1px solid ${t.palette.divider}`,
                            })}
                        >
                            <Stack spacing={3} alignItems="center" sx={{ width: "100%" }}>
                                <Box
                                    component="img"
                                    src="/pims_logo.jpg"
                                    alt="PIMS Elektro Oprema"
                                    sx={{
                                        maxWidth: "60%",
                                        maxHeight: 260,
                                        objectFit: "contain",
                                        borderRadius: 2,
                                        boxShadow: (t) =>
                                            t.palette.mode === "dark"
                                                ? "0 12px 32px rgba(0,0,0,0.5)"
                                                : "0 12px 32px rgba(15,23,42,0.1)",
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary", textAlign: "center" }}
                                >
                                    {translations.pimsVersion ||
                                        "PIMS Elektro Oprema d.o.o. — Edition v1.0"}
                                </Typography>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default LoginPage;